import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Plus, Edit2, Trash2, Save, X, MapPin, Phone, Globe, Instagram, Facebook, Twitter } from 'lucide-react';
import supabase from '../supabase';

const VenueManager = ({ editVenueId }) => {
  const [venues, setVenues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingId, setEditingId] = useState(editVenueId || null);
  const editFormRef = useRef(null);
  const venuesSectionRef = useRef(null);
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    phone: '',
    website: '',
    socialMedia: {
      instagram: '',
      facebook: '',
      twitter: ''
    }
  });

  const loadVenues = useCallback(async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('venues')
        .select('*')
        .order('name');

      if (error) {
        console.error('Error loading venues:', error);
        console.error('Error details:', error.message);
        setVenues([]);
        return;
      }

      if (data && data.length > 0) {
        setVenues(data);
      } else {
        // Initialize with default venues if table is empty
        await initializeDefaultVenues();
      }
    } catch (error) {
      console.error('Error loading venues:', error);
      setVenues([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Load venues from Supabase
  useEffect(() => {
    loadVenues();
  }, [loadVenues]);

  const initializeDefaultVenues = async () => {
    const defaultVenues = [
      {
        name: 'The Echoplex',
        address: '1822 Sunset Blvd, Los Angeles, CA 90026',
        phone: '(213) 413-8200',
        website: 'https://www.spacelandpresents.com/echoplex',
        social_media: {
          instagram: '@echoplexla',
          facebook: 'EchoplexLA',
          twitter: '@echoplexla'
        }
      },
      {
        name: 'The Smell',
        address: '247 S Main St, Los Angeles, CA 90012',
        phone: '',
        website: 'https://www.thesmell.org',
        social_media: {
          instagram: '@thesmellvenue',
          facebook: 'TheSmellVenue',
          twitter: ''
        }
      }
    ];

    try {
      const { data, error } = await supabase
        .from('venues')
        .insert(defaultVenues)
        .select();

      if (error) throw error;
      setVenues(data || []);
    } catch (error) {
      console.error('Error initializing default venues:', error);
    }
  };

  // Auto-scroll to venue being edited
  useEffect(() => {
    if (editVenueId && venues.length > 0) {
      // Start editing the venue first
      setEditingId(editVenueId);
      
      // Scroll to the edit form container
      setTimeout(() => {
        if (editFormRef.current) {
          editFormRef.current.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'start',
            inline: 'nearest'
          });
        }
      }, 200);
    }
  }, [editVenueId, venues]);

  const resetForm = () => {
    setFormData({
      name: '',
      address: '',
      phone: '',
      website: '',
      socialMedia: {
        instagram: '',
        facebook: '',
        twitter: ''
      }
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name.startsWith('social.')) {
      const socialField = name.split('.')[1];
      setFormData(prev => ({
        ...prev,
        socialMedia: {
          ...prev.socialMedia,
          [socialField]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const addVenue = async (venueData) => {
    try {
      const newVenue = {
        name: venueData.name,
        address: venueData.address,
        phone: venueData.phone,
        website: venueData.website,
        social_media: venueData.socialMedia
      };

      const { data, error } = await supabase
        .from('venues')
        .insert([newVenue])
        .select();

      if (error) throw error;

      if (data && data[0]) {
        setVenues(prev => [...prev, data[0]].sort((a, b) => a.name.localeCompare(b.name)));
        return data[0];
      }
    } catch (error) {
      console.error('Error adding venue:', error);
      console.error('Error details:', error.message);
      alert(`Failed to add venue: ${error.message}`);
    }
  };

  const updateVenue = async (id, venueData) => {
    try {
      const updatedVenue = {
        name: venueData.name,
        address: venueData.address,
        phone: venueData.phone,
        website: venueData.website,
        social_media: venueData.socialMedia
      };

      const { data, error } = await supabase
        .from('venues')
        .update(updatedVenue)
        .eq('id', id)
        .select();

      if (error) throw error;

      if (data && data[0]) {
        setVenues(prev => prev.map(v => v.id === id ? data[0] : v).sort((a, b) => a.name.localeCompare(b.name)));
      }
    } catch (error) {
      console.error('Error updating venue:', error);
      alert('Failed to update venue. Please try again.');
    }
  };

  const deleteVenue = async (id) => {
    try {
      const { error } = await supabase
        .from('venues')
        .delete()
        .eq('id', id);

      if (error) throw error;

      setVenues(prev => prev.filter(v => v.id !== id));
    } catch (error) {
      console.error('Error deleting venue:', error);
      alert('Failed to delete venue. Please try again.');
    }
  };

  const handleAddVenue = async () => {
    if (formData.name.trim()) {
      await addVenue(formData);
      resetForm();
      setShowAddForm(false);
    }
  };

  const handleEditVenue = (venue) => {
    setEditingId(venue.id);
    setFormData({
      name: venue.name || '',
      address: venue.address || '',
      phone: venue.phone || '',
      website: venue.website || '',
      socialMedia: {
        instagram: venue.social_media?.instagram || '',
        facebook: venue.social_media?.facebook || '',
        twitter: venue.social_media?.twitter || ''
      }
    });

    // Scroll to the edit form container
    setTimeout(() => {
      if (editFormRef.current) {
        editFormRef.current.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'start',
          inline: 'nearest'
        });
      }
    }, 100);
  };

  const handleSaveEdit = async () => {
    if (formData.name.trim()) {
      await updateVenue(editingId, formData);
      setEditingId(null);
      resetForm();
    }
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    resetForm();
  };

  const handleDeleteVenue = async (id) => {
    if (window.confirm('Are you sure you want to delete this venue? This may affect existing events.')) {
      await deleteVenue(id);
    }
  };

  const handleCancelAdd = () => {
    setShowAddForm(false);
    resetForm();
  };

  const handleAddFirstVenue = () => {
    setShowAddForm(true);
    // Scroll to top of the Manage Venues section
    if (venuesSectionRef.current) {
      venuesSectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const getSocialIcon = (platform) => {
    switch (platform) {
      case 'instagram': return <Instagram size={14} />;
      case 'facebook': return <Facebook size={14} />;
      case 'twitter': return <Twitter size={14} />;
      default: return null;
    }
  };

  return (
    <div 
      ref={venuesSectionRef}
      data-venue-container
      style={{
        background: 'rgba(255, 255, 255, 0.05)',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        borderRadius: '16px',
        padding: '2rem'
      }}
    >
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '2rem'
      }}>
        <div>
          <h2 style={{color: 'white', fontSize: '1.75rem', margin: '0 0 0.5rem 0'}}>
            Manage Venues
          </h2>
          <p style={{color: '#9ca3af', margin: 0}}>
            Add, edit, or remove venue information for your events
          </p>
        </div>
        <button
          onClick={() => setShowAddForm(true)}
          style={{
            background: 'linear-gradient(45deg, #10b981, #059669)',
            color: 'white',
            border: 'none',
            borderRadius: '12px',
            padding: '0.75rem 1.5rem',
            fontSize: '0.95rem',
            fontWeight: '600',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            transition: 'all 0.3s ease'
          }}
        >
          <Plus size={18} />
          Add Venue
        </button>
      </div>

      {/* Add/Edit Venue Form */}
      {(showAddForm || editingId) && (
        <div 
          ref={editFormRef}
          style={{
            background: 'rgba(16, 185, 129, 0.1)',
            border: '1px solid rgba(16, 185, 129, 0.2)',
            borderRadius: '12px',
            padding: '2rem',
            marginBottom: '2rem'
          }}
        >
          <h3 style={{color: '#10b981', fontSize: '1.1rem', margin: '0 0 1.5rem 0'}}>
            {editingId ? 'Edit Venue' : 'Add New Venue'}
          </h3>
          
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
            gap: '1rem',
            marginBottom: '1.5rem'
          }}>
            <div>
              <label style={{
                display: 'block',
                marginBottom: '0.5rem',
                fontWeight: '600',
                color: 'white',
                fontSize: '0.9rem'
              }}>
                Venue Name *
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                placeholder="Enter venue name"
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  borderRadius: '8px',
                  backgroundColor: 'rgba(255, 255, 255, 0.05)',
                  color: 'white',
                  fontSize: '1rem',
                  boxSizing: 'border-box'
                }}
              />
            </div>

            <div>
              <label style={{
                display: 'block',
                marginBottom: '0.5rem',
                fontWeight: '600',
                color: 'white',
                fontSize: '0.9rem'
              }}>
                Phone
              </label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                placeholder="(555) 123-4567"
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  borderRadius: '8px',
                  backgroundColor: 'rgba(255, 255, 255, 0.05)',
                  color: 'white',
                  fontSize: '1rem',
                  boxSizing: 'border-box'
                }}
              />
            </div>
          </div>

          <div style={{marginBottom: '1.5rem'}}>
            <label style={{
              display: 'block',
              marginBottom: '0.5rem',
              fontWeight: '600',
              color: 'white',
              fontSize: '0.9rem'
            }}>
              Address
            </label>
            <input
              type="text"
              name="address"
              value={formData.address}
              onChange={handleInputChange}
              placeholder="123 Main St, City, State 12345"
              style={{
                width: '100%',
                padding: '0.75rem',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                borderRadius: '8px',
                backgroundColor: 'rgba(255, 255, 255, 0.05)',
                color: 'white',
                fontSize: '1rem',
                boxSizing: 'border-box'
              }}
            />
          </div>

          <div style={{marginBottom: '1.5rem'}}>
            <label style={{
              display: 'block',
              marginBottom: '0.5rem',
              fontWeight: '600',
              color: 'white',
              fontSize: '0.9rem'
            }}>
              Website
            </label>
            <input
              type="url"
              name="website"
              value={formData.website}
              onChange={handleInputChange}
              placeholder="https://www.venue.com"
              style={{
                width: '100%',
                padding: '0.75rem',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                borderRadius: '8px',
                backgroundColor: 'rgba(255, 255, 255, 0.05)',
                color: 'white',
                fontSize: '1rem',
                boxSizing: 'border-box'
              }}
            />
          </div>

          <div style={{marginBottom: '2rem'}}>
            <h4 style={{color: 'white', marginBottom: '1rem', fontSize: '1rem'}}>
              Social Media
            </h4>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: '1rem'
            }}>
              <div>
                <label style={{
                  display: 'block',
                  marginBottom: '0.5rem',
                  fontWeight: '600',
                  color: 'white',
                  fontSize: '0.85rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}>
                  <Instagram size={14} />
                  Instagram
                </label>
                <input
                  type="text"
                  name="social.instagram"
                  value={formData.socialMedia.instagram}
                  onChange={handleInputChange}
                  placeholder="@username"
                  style={{
                    width: '100%',
                    padding: '0.6rem',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    borderRadius: '6px',
                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                    color: 'white',
                    fontSize: '0.9rem',
                    boxSizing: 'border-box'
                  }}
                />
              </div>

              <div>
                <label style={{
                  display: 'block',
                  marginBottom: '0.5rem',
                  fontWeight: '600',
                  color: 'white',
                  fontSize: '0.85rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}>
                  <Facebook size={14} />
                  Facebook
                </label>
                <input
                  type="text"
                  name="social.facebook"
                  value={formData.socialMedia.facebook}
                  onChange={handleInputChange}
                  placeholder="VenueName"
                  style={{
                    width: '100%',
                    padding: '0.6rem',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    borderRadius: '6px',
                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                    color: 'white',
                    fontSize: '0.9rem',
                    boxSizing: 'border-box'
                  }}
                />
              </div>

              <div>
                <label style={{
                  display: 'block',
                  marginBottom: '0.5rem',
                  fontWeight: '600',
                  color: 'white',
                  fontSize: '0.85rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}>
                  <Twitter size={14} />
                  Twitter/X
                </label>
                <input
                  type="text"
                  name="social.twitter"
                  value={formData.socialMedia.twitter}
                  onChange={handleInputChange}
                  placeholder="@username"
                  style={{
                    width: '100%',
                    padding: '0.6rem',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    borderRadius: '6px',
                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                    color: 'white',
                    fontSize: '0.9rem',
                    boxSizing: 'border-box'
                  }}
                />
              </div>
            </div>
          </div>

          <div style={{display: 'flex', gap: '1rem', justifyContent: 'flex-end'}}>
            <button
              onClick={editingId ? handleCancelEdit : handleCancelAdd}
              style={{
                background: 'rgba(239, 68, 68, 0.2)',
                color: '#ef4444',
                border: '1px solid rgba(239, 68, 68, 0.3)',
                borderRadius: '8px',
                padding: '0.75rem 1.5rem',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}
            >
              <X size={16} />
              Cancel
            </button>
            <button
              onClick={editingId ? handleSaveEdit : handleAddVenue}
              disabled={!formData.name.trim()}
              style={{
                background: 'linear-gradient(45deg, #10b981, #059669)',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                padding: '0.75rem 1.5rem',
                cursor: formData.name.trim() ? 'pointer' : 'not-allowed',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                opacity: formData.name.trim() ? 1 : 0.5
              }}
            >
              <Save size={16} />
              {editingId ? 'Update Venue' : 'Save Venue'}
            </button>
          </div>
        </div>
      )}

      {/* Venues Stats */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: '1rem',
        marginBottom: '2rem'
      }}>
        <div style={{
          background: 'rgba(255, 0, 128, 0.1)',
          border: '1px solid rgba(255, 0, 128, 0.2)',
          borderRadius: '12px',
          padding: '1.5rem',
          textAlign: 'center'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '48px',
            height: '48px',
            background: 'linear-gradient(45deg, #ff0080, #7928ca)',
            borderRadius: '12px',
            color: 'white',
            margin: '0 auto 0.75rem'
          }}>
            <MapPin size={20} />
          </div>
          <h3 style={{fontSize: '1.75rem', fontWeight: '900', color: 'white', margin: '0 0 0.25rem 0'}}>
            {venues.length}
          </h3>
          <p style={{color: '#9ca3af', fontWeight: '500', margin: 0}}>
            Total Venues
          </p>
        </div>
      </div>

      {/* Venues List */}
      {loading ? (
        <div style={{
          textAlign: 'center',
          padding: '3rem 2rem',
          color: 'white'
        }}>
          Loading venues...
        </div>
      ) : venues.length === 0 ? (
        <div style={{
          textAlign: 'center',
          padding: '3rem 2rem',
          background: 'rgba(255, 255, 255, 0.02)',
          border: '2px dashed rgba(255, 255, 255, 0.1)',
          borderRadius: '12px'
        }}>
          <MapPin size={48} style={{color: '#6b7280', marginBottom: '1rem'}} />
          <h3 style={{color: 'white', fontSize: '1.25rem', marginBottom: '0.5rem'}}>
            No venues yet
          </h3>
          <p style={{color: '#9ca3af', marginBottom: '1.5rem'}}>
            Add your first venue to get started
          </p>
          <button
            onClick={handleAddFirstVenue}
            style={{
              background: 'linear-gradient(45deg, #10b981, #059669)',
              color: 'white',
              border: 'none',
              borderRadius: '12px',
              padding: '0.75rem 1.5rem',
              fontSize: '0.95rem',
              fontWeight: '600',
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}
          >
            <Plus size={18} />
            Add First Venue
          </button>
        </div>
      ) : (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))',
          gap: '1.5rem'
        }}>
          {venues.map((venue) => (
            <div
              key={venue.id}
              id={`venue-${venue.id}`}
              style={{
                background: 'rgba(255, 255, 255, 0.03)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                borderRadius: '12px',
                padding: '1.5rem',
                transition: 'all 0.3s ease'
              }}
            >
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                marginBottom: '1rem'
              }}>
                <h3 style={{
                  color: 'white',
                  fontSize: '1.1rem',
                  fontWeight: '600',
                  margin: 0,
                  flex: 1
                }}>
                  {venue.name}
                </h3>
                <div style={{display: 'flex', gap: '0.5rem'}}>
                  <button
                    onClick={() => handleEditVenue(venue)}
                    style={{
                      background: 'rgba(59, 130, 246, 0.2)',
                      color: '#3b82f6',
                      border: '1px solid rgba(59, 130, 246, 0.3)',
                      borderRadius: '6px',
                      padding: '0.4rem',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <Edit2 size={12} />
                  </button>
                  <button
                    onClick={() => handleDeleteVenue(venue.id)}
                    style={{
                      background: 'rgba(239, 68, 68, 0.2)',
                      color: '#ef4444',
                      border: '1px solid rgba(239, 68, 68, 0.3)',
                      borderRadius: '6px',
                      padding: '0.4rem',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <Trash2 size={12} />
                  </button>
                </div>
              </div>

              {venue.address && (
                <div style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '0.5rem',
                  marginBottom: '0.75rem'
                }}>
                  <MapPin size={14} style={{color: '#9ca3af', marginTop: '0.1rem', flexShrink: 0}} />
                  <span style={{color: '#9ca3af', fontSize: '0.9rem', lineHeight: '1.4'}}>
                    {venue.address}
                  </span>
                </div>
              )}

              {venue.phone && (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  marginBottom: '0.75rem'
                }}>
                  <Phone size={14} style={{color: '#9ca3af'}} />
                  <span style={{color: '#9ca3af', fontSize: '0.9rem'}}>
                    {venue.phone}
                  </span>
                </div>
              )}

              {venue.website && (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  marginBottom: '1rem'
                }}>
                  <Globe size={14} style={{color: '#9ca3af'}} />
                  <a 
                    href={venue.website} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    style={{
                      color: '#3b82f6',
                      fontSize: '0.9rem',
                      textDecoration: 'none'
                    }}
                  >
                    Visit Website
                  </a>
                </div>
              )}

              {/* Social Media Links */}
              {venue.social_media && Object.values(venue.social_media).some(value => value) && (
                <div style={{
                  borderTop: '1px solid rgba(255, 255, 255, 0.1)',
                  paddingTop: '1rem'
                }}>
                  <h4 style={{
                    color: '#9ca3af',
                    fontSize: '0.8rem',
                    fontWeight: '600',
                    margin: '0 0 0.5rem 0',
                    textTransform: 'uppercase'
                  }}>
                    Social Media
                  </h4>
                  <div style={{display: 'flex', gap: '0.75rem'}}>
                    {venue.social_media.instagram && (
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.25rem',
                        color: '#9ca3af',
                        fontSize: '0.85rem'
                      }}>
                        {getSocialIcon('instagram')}
                        <span>{venue.social_media.instagram}</span>
                      </div>
                    )}
                    {venue.social_media.facebook && (
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.25rem',
                        color: '#9ca3af',
                        fontSize: '0.85rem'
                      }}>
                        {getSocialIcon('facebook')}
                        <span>{venue.social_media.facebook}</span>
                      </div>
                    )}
                    {venue.social_media.twitter && (
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.25rem',
                        color: '#9ca3af',
                        fontSize: '0.85rem'
                      }}>
                        {getSocialIcon('twitter')}
                        <span>{venue.social_media.twitter}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default VenueManager;
